import { useEffect, useMemo, useRef, useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import HelpIconButton from "../../Buttons/Iconbuttons/HelpIconButton";
import FertilizerTableField from "../../FormInputElements/FertilizerTableField";
import { useReferenceData } from "../../../hooks/useReferenceData";

const EMPTY_FERTILIZER_RATE = {
  pre_seed: "",
  with_seed: "",
  post_seed: "",
};

const NUTRIENT_ORDER = ["N", "P", "K", "S"];

function nutrientRank(value) {
  const index = NUTRIENT_ORDER.indexOf(value);
  return index === -1 ? NUTRIENT_ORDER.length : index;
}

function sortNutrients(values) {
  return [...values].sort((left, right) => {
    return (
      nutrientRank(left) - nutrientRank(right) ||
      String(left).localeCompare(String(right))
    );
  });
}

function getSelectedNutrients(fertilizerRates) {
  const selected = Object.keys(fertilizerRates || {}).filter((value) => {
    const rate = fertilizerRates?.[value];
    return ["pre_seed", "with_seed", "post_seed"].some((key) => {
      const currentValue = rate?.[key];
      return currentValue !== "" && currentValue !== null && currentValue !== undefined;
    });
  });

  return selected.length > 0 ? sortNutrients(selected) : ["N"];
}

export default function FertilizerRatesForm({ cropYear, setCropYear }) {
  const { referenceData } = useReferenceData(["fertilizer_nutrients"]);
  const orderedFertilizerTypes = useMemo(
    () =>
      (referenceData.fertilizer_nutrients || [])
        .filter((item) => NUTRIENT_ORDER.includes(item.value))
        .sort(
          (left, right) =>
            nutrientRank(left.value) - nutrientRank(right.value)
        ),
    [referenceData.fertilizer_nutrients]
  );
  const previousCropYearId = useRef(cropYear.id);
  const [selectedNutrients, setSelectedNutrients] = useState(() =>
    getSelectedNutrients(cropYear.fertilizer_rates)
  );

  useEffect(() => {
    if (previousCropYearId.current === cropYear.id) {
      return;
    }

    previousCropYearId.current = cropYear.id;
    setSelectedNutrients(getSelectedNutrients(cropYear.fertilizer_rates));
  }, [cropYear.id, cropYear.fertilizer_rates]);

  function handleChange(value, nutrientCode, stage) {
    setCropYear({
      ...cropYear,
      fertilizer_rates: {
        ...(cropYear.fertilizer_rates || {}),
        [nutrientCode]: {
          ...(cropYear.fertilizer_rates?.[nutrientCode] || EMPTY_FERTILIZER_RATE),
          [stage]: value,
        },
      },
    });
  }

  function handleToggleNutrient(event) {
    const nutrientCode = event.target.name;
    const isChecked = event.target.checked;

    setSelectedNutrients((current) => {
      if (isChecked) {
        if (current.includes(nutrientCode)) {
          return current;
        }
        return sortNutrients([...current, nutrientCode]);
      }

      return current.filter((item) => item !== nutrientCode);
    });

    if (!isChecked) {
      setCropYear({
        ...cropYear,
        fertilizer_rates: {
          ...(cropYear.fertilizer_rates || {}),
          [nutrientCode]: { ...EMPTY_FERTILIZER_RATE },
        },
      });
    }
  }

  return (
    <section className="mt-5 border-t border-slate-200 pt-5">
      <div className="mb-3 flex w-full text-[#666666]">
        <div className="w-full">
          <h4 className="text-[19px] font-bold text-slate-900">
            Standard nutrient rates
          </h4>
          <p className="mt-1 text-sm leading-6 text-slate-600">
            Enter the amount of each nutrient applied per acre. Use the detailed option above when you have product-level application records.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <p className="mt-3 text-sm font-bold text-slate-700">
              Nutrients applied
            </p>
            <HelpIconButton
              title="Fertilizer Rates"
              description="Select each nutrient you applied, then enter the rate for the stage when it was applied."
            />
          </div>
          <div className="mt-1 flex flex-wrap gap-x-2 border-y border-slate-200 bg-slate-50 px-2 py-1">
            {orderedFertilizerTypes.map((fertilizer) => (
              <FormControlLabel
                key={fertilizer.value}
                control={
                  <Checkbox
                    checked={selectedNutrients.includes(fertilizer.value)}
                    name={fertilizer.value}
                    onChange={handleToggleNutrient}
                    sx={{
                      color: "rgb(102,102,102)",
                      "&.Mui-checked": {
                        color: "rgb(241,93,34)",
                      },
                    }}
                  />
                }
                label={fertilizer.label}
                sx={{ mr: 1 }}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto border-b border-slate-200">
        <div className="grid min-w-[700px] grid-cols-[minmax(180px,1.2fr)_repeat(3,minmax(130px,1fr))_100px] bg-slate-50 text-xs font-bold uppercase text-slate-500">
          <div className="px-3 py-2">Nutrient</div>
          <div className="px-3 py-2">Pre-seed</div>
          <div className="px-3 py-2">At seeding</div>
          <div className="px-3 py-2">Post-seed</div>
          <div className="px-3 py-2 text-right">Total lb/ac</div>
        </div>
        {sortNutrients(selectedNutrients).map((nutrientCode) => {
          const fertilizer = orderedFertilizerTypes.find((item) => item.value === nutrientCode);
          if (!fertilizer) return null;
          return (
            <FertilizerTableField
              key={nutrientCode}
              fertilizerName={fertilizer.label}
              onChange={handleChange}
              elementCode={nutrientCode}
              fieldState={cropYear.fertilizer_rates?.[nutrientCode]}
            />
          );
        })}
      </div>
    </section>
  );
}
